import { useState } from "react";
import { Loader2, Pencil, X, Check, Star } from "lucide-react";
import { api } from "../lib/nailApi";

/**
 * Phase 8.12.2 — EditRegistrationDrawer
 *
 * Right-side drawer opened by clicking the pencil icon on any registration row
 * in the admin table. Edits whitelisted fields via `PATCH /api/admin/nail-events/
 * {slug}/registrations/{id}`. Every change is server-side audit-logged with
 * admin email + before/after + IP.
 *
 * Fields exposed:
 *   • business_name
 *   • owner_name
 *   • phone (re-normalizes phone_normalized server-side)
 *   • email
 *   • preferred_language
 *   • guest_count
 *   • admin_notes  (Phase 8.12.3)
 *   • is_vip        (Phase 8.12.3)
 */
export default function EditRegistrationDrawer({ slug, registration, onClose, onSaved, lang = "vi" }) {
  const L = (vi, en) => (lang === "en" ? en : vi);
  const [form, setForm] = useState({
    business_name: registration.business_name || "",
    owner_name: registration.owner_name || "",
    phone: registration.phone || "",
    email: registration.email || "",
    preferred_language: registration.preferred_language || "vi",
    guest_count: registration.guest_count || 1,
    admin_notes: registration.admin_notes || "",
    is_vip: !!registration.is_vip,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!form.business_name.trim()) return setError(L("Tên tiệm không được để trống.", "Business name required."));
    setBusy(true);
    try {
      await api.patch(`/admin/nail-events/${slug}/registrations/${registration.id}`, {
        business_name: form.business_name.trim(),
        owner_name: form.owner_name.trim() || null,
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        preferred_language: form.preferred_language,
        guest_count: Math.max(1, Number(form.guest_count) || 1),
        admin_notes: form.admin_notes || null,
        is_vip: form.is_vip,
      });
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || L("Có lỗi xảy ra.", "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="edit-registration-drawer"
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-end bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="w-full sm:max-w-md h-full sm:h-auto sm:max-h-[92vh] bg-white sm:rounded-l-3xl rounded-t-3xl sm:rounded-tr-none shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-espresso/10 bg-linen-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-espresso text-linen">
              <Pencil className="w-4 h-4" />
            </span>
            <div>
              <div className="font-serif text-lg text-espresso leading-tight">
                {L("Sửa đăng ký", "Edit registration")}
              </div>
              <div className="text-[11px] text-espresso-soft">
                #{registration.id} · {registration.phone_normalized}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-close"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-linen-200 text-espresso-soft"
            aria-label={L("Đóng", "Close")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {/* VIP toggle — prominent */}
          <label
            data-testid="edit-vip-toggle-row"
            className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 cursor-pointer select-none transition ${
              form.is_vip
                ? "border-gold-400 bg-gold-100/50"
                : "border-espresso/15 bg-white hover:bg-linen-100"
            }`}
          >
            <input
              type="checkbox"
              checked={form.is_vip}
              onChange={(e) => update("is_vip", e.target.checked)}
              data-testid="edit-vip-toggle"
              className="w-4 h-4 accent-gold-500"
            />
            <Star className={`w-4 h-4 ${form.is_vip ? "text-gold-500 fill-gold-400" : "text-espresso-soft"}`} />
            <span className="text-sm font-semibold text-espresso">
              {L("Khách VIP", "VIP guest")}
            </span>
            <span className="text-[11px] text-espresso-soft ml-auto">
              {L("Nhân viên thấy trước khi khách tới cửa", "Staff see this before door arrival")}
            </span>
          </label>

          <Field label={L("Tên tiệm *", "Business name *")}>
            <input value={form.business_name} onChange={(e) => update("business_name", e.target.value)}
              data-testid="edit-business-name"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
          </Field>
          <Field label={L("Tên chủ tiệm", "Owner name")}>
            <input value={form.owner_name} onChange={(e) => update("owner_name", e.target.value)}
              data-testid="edit-owner-name"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={L("Điện thoại", "Phone")}>
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)}
                data-testid="edit-phone"
                inputMode="tel"
                className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-espresso" />
            </Field>
            <Field label={L("Số khách", "Guest count")}>
              <input type="number" min={1} max={20} value={form.guest_count} onChange={(e) => update("guest_count", e.target.value)}
                data-testid="edit-guest-count"
                className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
            </Field>
          </div>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
              data-testid="edit-email"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso" />
          </Field>
          <Field label={L("Ngôn ngữ", "Language")}>
            <select value={form.preferred_language} onChange={(e) => update("preferred_language", e.target.value)}
              data-testid="edit-preferred-lang"
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso">
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
            </select>
          </Field>

          {/* Admin notes */}
          <Field label={L("Ghi chú admin (chỉ nội bộ)", "Admin notes (internal only)")}>
            <textarea value={form.admin_notes} onChange={(e) => update("admin_notes", e.target.value)}
              rows={3}
              data-testid="edit-admin-notes"
              placeholder={L("VD: cần hỗ trợ tiếng Việt, ăn chay, đến sớm 30 phút…", "e.g. needs VN interpreter, vegan, arriving 30min early…")}
              className="w-full bg-white border border-espresso/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-espresso resize-none" />
          </Field>

          {error && (
            <div data-testid="edit-error" className="rounded-xl bg-[#FBEDEB] border border-[#E9C6C1] text-[#7A2E2E] text-sm px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 bg-linen-100 border-t border-espresso/10 flex items-center justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="edit-cancel"
            className="px-4 py-2 text-sm font-medium text-espresso-soft hover:text-espresso rounded-full transition">
            {L("Hủy", "Cancel")}
          </button>
          <button type="submit" disabled={busy}
            data-testid="edit-save"
            className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-espresso-hover disabled:opacity-50 transition">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {L("Lưu thay đổi", "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
